import Vue from "vue";
import i18n from "@/i18n";
import Axios from "axios";
import _ from "@/boot/lodash";
import { ILanguage } from "@/models/constants";
import { ActionTree, MutationTree } from "vuex";
import { localize } from "vee-validate";
import { ToastProgrammatic as $toast } from "buefy";
import { IState } from "@/store";

export interface Ii18nState {
  locales: ILocale[];
  activeLocaleCode: string;
}

export interface ILocale {
  language: ILanguage;
  packs: any[];
}

const initialState: Ii18nState = {
  locales: [
    {
      language: {
        code: "en",
        language: "English"
      } as ILanguage,
      packs: []
    }
  ],
  activeLocaleCode: localStorage.getItem("i18n/active_locale_code") || "en"
};

const actions: ActionTree<Ii18nState, IState> = {
  setLanguage: (
    { state, commit, dispatch },
    { localeCode }: { localeCode: string }
  ) => {
    return new Promise(resolve => {
      if (localeCode === state.activeLocaleCode) {
        dispatch("loadValidationLocale", { localeCode });
        resolve(localeCode);
        return;
      }

      dispatch("loadLanguagePacks", {
        localeCode,
        packs: [
          "_",
          "_action",
          "_error",
          "_hook",
          "_sentence",
          "_validation",
          "_placeholder",
          "_datetime",
          "_plurals"
        ]
      }).then(() => {
        if (localeCode !== "en") {
          require(`moment/locale/${localeCode}`);
        }
        Vue.prototype.$moment.locale(localeCode);
        i18n.locale = localeCode;
        commit("setLocaleCode", { localeCode });
        dispatch("loadValidationLocale", { localeCode });

        // @ts-ignore
        document.querySelector("html").setAttribute("lang", localeCode);

        $toast.open({
          message: i18n.t("_.language_changed") as string,
          position: "is-bottom",
          queue: false
        });

        resolve(localeCode);
      });
    });
  },

  loadLanguagePacks: (
    { dispatch, commit },
    { localeCode = i18n.locale, packs }: { localeCode: string; packs: [] }
  ) => {
    return new Promise<void>(resolve => {
      Promise.all(
        _.map(packs, pack => {
          return dispatch("loadLanguagePack", { localeCode, pack });
        })
      ).then(packs => {
        const messages = {};
        _.each(packs, pack => {
          if (!pack) return;

          /**
           * Translates files with dot in the name into nested objects
           * .ie _modules.web_hosting => {_modules: {web_hosting: {...}}}
           */
          const paths = pack.name.split(".");
          let curr = messages;
          while (paths.length) {
            const path = paths.shift();
            if (!paths.length) curr[path] = pack.messages;
            else {
              curr[path] = {};
              curr = curr[path];
            }
          }

          commit("addLocalePack", {
            localeCode,
            pack: pack.name
          });
        });
        if (!_.isEmpty(messages)) {
          i18n.mergeLocaleMessage(localeCode, messages);
          i18n.setLocaleMessage(localeCode, i18n.messages[localeCode]);
        }
        resolve();
      });
    });
  },

  fetchLanguageFile: (
    ctx,
    {
      localeCode = i18n.locale,
      name,
      path
    }: { localeCode: string; name: string; path?: string }
  ) => {
    const version = process.env.VERSION;

    return new Promise(resolve => {
      Axios.get(path || `/languages/${localeCode}/${name}.json?${version}`)
        .then(response => {
          resolve({
            name,
            messages: response.data
          });
        })
        .catch(() => {
          $toast.open({
            message: i18n.t("_sentence.language_pack_missing", {
              pack: `${localeCode}/${name}`
            }) as string,
            type: "is-danger"
          });
          resolve(null); // Pack not found
        });
    });
  },

  loadLanguagePack: (
    { dispatch, state, commit },
    {
      localeCode = i18n.locale,
      path,
      pack
    }: { localeCode: string; path: null; pack: [] }
  ) => {
    return new Promise(resolve => {
      let locale = _.find(
        state.locales,
        (l: ILocale) => l.language.code === localeCode
      ) as ILocale | undefined;

      // Check for invalid locale
      if (locale === undefined) {
        locale = {
          language: {
            code: localeCode
          } as ILanguage,
          packs: []
        } as ILocale;
        commit("addLocale", { locale });
      }

      // Pack already loaded
      if (locale.packs.includes(pack)) {
        return resolve(null);
      }

      // Get language pack
      dispatch("fetchLanguageFile", {
        localeCode,
        name: pack,
        path
      }).then(resolve);
    });
  },

  loadValidationLocale: (
    // eslint-disable-next-line no-empty-pattern
    {},
    { localeCode = i18n.locale }: { localeCode: string }
  ) => {
    return import(`vee-validate/dist/locale/${localeCode}.json`).then(
      locale => {
        localize(localeCode, locale);
      }
    );
  }
};

const mutations: MutationTree<Ii18nState> = {
  addLocale: (state: Ii18nState, payload: { locale: ILocale }) => {
    const index = _.findIndex(
      state.locales,
      (l: ILocale) => l.language.code === payload.locale.language.code
    );

    if (index !== -1) {
      _.merge(state.locales[index], payload.locale);
      return;
    }
    state.locales.push(payload.locale);
  },
  setLocaleCode: (state: Ii18nState, payload: { localeCode: string }) => {
    state.activeLocaleCode = payload.localeCode;
    localStorage.setItem("i18n/active_locale_code", payload.localeCode);
  },
  addLocalePack: (
    state: Ii18nState,
    { localeCode, pack }: { localeCode: string; pack: string }
  ) => {
    const index = _.findIndex(
      state.locales,
      (l: ILocale) => l.language.code === localeCode
    );

    // Check for invalid locale
    if (index === -1) return false;
    state.locales[index].packs.push(pack);
  }
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations
};
